import type { User } from "@/lib/api/user";
import { isMenteeIdentity, isMentorIdentity } from "@/lib/utils/system-roles";

export const USER_RECORD_TAB_IDS = [
  "account",
  "mentorships",
  "performance",
  "matching",
  "first-moments",
  "schedule",
  "supervision",
  "care",
  "roles",
] as const;

export type UserRecordTabId = (typeof USER_RECORD_TAB_IDS)[number];

export const SELF_VIEW_TAB_IDS = [
  "account",
  "matching",
  "first-moments",
  "schedule",
] as const;

export type SelfViewTabId = (typeof SELF_VIEW_TAB_IDS)[number];

export type UserRecordSurface = "self" | "record";

export interface UserRecordTab {
  id: UserRecordTabId;
  label: string;
}

export const SELF_TAB_ROUTES = {
  account: "/profile/account",
  matching: "/profile/matching",
  "first-moments": "/profile/first-moments",
  schedule: "/profile/schedule",
} as const satisfies Record<SelfViewTabId, string>;

const TAB_LABELS: Record<UserRecordTabId, string> = {
  account: "Account",
  mentorships: "Mentorship",
  performance: "Performance",
  matching: "Profile",
  "first-moments": "First Moments",
  schedule: "Schedule",
  supervision: "Supervision",
  care: "Care",
  roles: "Roles & Permissions",
};

function tab(id: UserRecordTabId, label = TAB_LABELS[id]): UserRecordTab {
  return { id, label };
}

export function isSelfViewTabId(id: UserRecordTabId): id is SelfViewTabId {
  return (SELF_VIEW_TAB_IDS as readonly string[]).includes(id);
}

export function selfTabFromPath(pathname: string): SelfViewTabId {
  const match = (
    Object.entries(SELF_TAB_ROUTES) as [SelfViewTabId, string][]
  ).find(([, path]) => path === pathname);
  return match?.[0] ?? "account";
}

export function isMentorOnlySelfPath(pathname: string): boolean {
  return (
    pathname === SELF_TAB_ROUTES.matching ||
    pathname === SELF_TAB_ROUTES["first-moments"] ||
    pathname === SELF_TAB_ROUTES.schedule
  );
}

export function getSelfViewTabs(user: User | undefined): UserRecordTab[] {
  if (isMentorIdentity(user?.systemRoles)) {
    return [
      tab("matching"),
      tab("first-moments"),
      tab("schedule"),
      tab("account"),
    ];
  }
  return [tab("account")];
}

export function shouldShowSupervision(params: {
  roleNames: string[] | undefined;
  isSupervisor?: boolean;
  canManageSupervisorAssignments: boolean;
  canReadMentorships?: boolean;
  hasSupervisedMentors?: boolean;
}): boolean {
  const roles = params.roleNames ?? [];
  const supervises =
    roles.includes("lead_mentor") ||
    roles.includes("supervisor") ||
    Boolean(params.isSupervisor);
  if (!supervises) return false;
  if (params.canManageSupervisorAssignments) return true;
  return Boolean(params.canReadMentorships && params.hasSupervisedMentors);
}

export function getRecordTabs(params: {
  user: User;
  canReadMentorships: boolean;
  canViewRoles?: boolean;
  showSupervision?: boolean;
  showCare?: boolean;
}): UserRecordTab[] {
  const {
    user,
    canReadMentorships,
    canViewRoles = false,
    showSupervision = false,
    showCare = false,
  } = params;
  const isMentor = isMentorIdentity(user.systemRoles);
  const isMentee = isMenteeIdentity(user.systemRoles);
  const mentorshipTab = canReadMentorships ? [tab("mentorships")] : [];

  const tabs: UserRecordTab[] = [];

  if (isMentor) {
    tabs.push(
      tab("performance"),
      ...mentorshipTab,
      tab("matching"),
      tab("first-moments"),
      tab("schedule"),
    );
    if (showSupervision) {
      tabs.push(tab("supervision"));
    }
    tabs.push(tab("account"));
  } else if (isMentee) {
    if (showCare) tabs.push(tab("care"));
    tabs.push(...mentorshipTab, tab("account"));
  } else if (showSupervision) {
    tabs.push(tab("supervision"), tab("account"));
  } else {
    tabs.push(tab("account"));
  }

  if (canViewRoles) {
    tabs.push(tab("roles"));
  }

  return tabs;
}

export function defaultTabId(tabs: UserRecordTab[]): UserRecordTabId {
  return tabs[0]?.id ?? "account";
}
